import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from "react-router-dom";
import { Component, Suspense, lazy, type ReactNode } from "react";
import ScrollToTop from "./components/ScrollToTop";
import PageViewBeacon from "./components/PageViewBeacon";
import StagingBanner from "./components/StagingBanner";
import CookieConsent from "./components/consent/CookieConsent";
import LandingPage from "./pages/landing/LandingPage";

// Only the public landing page ("/") is eager — it's the primary entry point,
// so it must not pay an extra round-trip. Every other route is code-split so a
// landing visitor no longer downloads admin, support, and secondary-lead code
// in the main bundle.
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminAuth = lazy(() => import("./pages/admin/AdminAuth"));
const LeadsAdmin = lazy(() => import("./pages/admin/LeadsAdmin"));
const EmailPreview = lazy(() => import("./pages/admin/EmailPreview"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const GetTheApp = lazy(() => import("./pages/GetTheApp"));
const SupportLanding = lazy(() => import("./pages/support/SupportLanding"));
const SupportTopicRedirect = lazy(() => import("./pages/support/SupportTopicRedirect"));
const MarketplaceClosed = lazy(() => import("./pages/support/MarketplaceClosed"));
const BecomeAMember = lazy(() => import("./pages/leads/BecomeAMember"));
const BecomeACreator = lazy(() => import("./pages/leads/BecomeACreator"));
const BecomeACreatorForm = lazy(() => import("./pages/leads/BecomeACreatorForm"));
const OCLacrosse2026 = lazy(() => import("./pages/events/OCLacrosse2026"));
const OCLacrosseConcept = lazy(() => import("./pages/events/OCLacrosseConcept"));

const queryClient = new QueryClient();

const CHUNK_ERROR =
  /dynamically imported module|module script failed|ChunkLoadError|Loading chunk|Failed to fetch/i;

// When a new version is deployed, a still-open SPA can request a code-split
// chunk whose hashed filename no longer exists, throwing on lazy import. This
// boundary detects that specific failure and does a one-shot hard reload to
// pull the new manifest. Any other error is re-thrown so it surfaces exactly as
// it did before code-splitting (no silent swallowing).
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    if (CHUNK_ERROR.test(message) && typeof window !== "undefined") {
      const KEY = "fs-chunk-reloaded";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.error) {
      const message =
        this.state.error instanceof Error
          ? this.state.error.message
          : String(this.state.error);
      // Stale-chunk error: a reload is already in flight, render nothing.
      if (CHUNK_ERROR.test(message)) return null;
      // Anything else: propagate so it behaves as it did pre-splitting.
      throw this.state.error;
    }
    return this.props.children;
  }
}

const RootLayout = () => (
  <>
    <ScrollToTop />
    <PageViewBeacon />
    <ChunkErrorBoundary>
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </ChunkErrorBoundary>
  </>
);

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <LandingPage /> },
      { path: "/privacy", element: <PrivacyPolicy /> },
      { path: "/terms", element: <TermsOfUse /> },
      { path: "/delete-account", element: <DeleteAccount /> },
      { path: "/app", element: <GetTheApp /> },
      { path: "/support", element: <SupportLanding /> },
      { path: "/support/marketplace-closed", element: <MarketplaceClosed /> },
      { path: "/support/category/:slug", element: <Navigate to="/support" replace /> },
      { path: "/support/:slug", element: <SupportTopicRedirect /> },
      { path: "/register", element: <Navigate to="/" replace /> },
      { path: "/oc-lacrosse-2026", element: <OCLacrosse2026 /> },
      // "Full Tilt" design concept for the same weekend. Standalone, noindex,
      // linked from nowhere — it exists to be sent as a link.
      { path: "/oc-lacrosse-concept", element: <OCLacrosseConcept /> },
      { path: "/become-a-member", element: <BecomeAMember /> },
      // The full creator marketing page (formerly the WIP /become-a-creator-preview).
      { path: "/become-a-creator", element: <BecomeACreator /> },
      // The standalone "just the registration form" page, kept reachable for
      // direct links (the marketing page embeds the same form in its CTA).
      { path: "/become-a-creator/apply", element: <BecomeACreatorForm /> },
      // The old preview path now lands on the live creator page.
      { path: "/become-a-creator-preview", element: <Navigate to="/become-a-creator" replace /> },
      { path: "/admin/auth", element: <AdminAuth /> },
      { path: "/admin/leads", element: <LeadsAdmin /> },
      { path: "/admin/email-preview", element: <EmailPreview /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <StagingBanner />
      <CookieConsent />
      <RouterProvider router={router} />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
